/**
 * /field/downhole — the strings a field hand works with: in the hole, back from
 * the well waiting to be torn down, and built and ready to go out.
 *
 * "THEIRS" IS NOT AN ASSIGNMENT, BECAUSE THE API HAS NONE. An assembly carries
 * no crew or tech; only a RUN names a `field_tech_id`. So this lists the
 * company's working strings — what a hand on any rig of this outfit may be
 * asked to run or break down — and marks the ones where the hand's own run is
 * open, first. It does not claim the others belong to someone else.
 *
 * `building` and `disassembled` are left out: a string being made up is shop
 * work at the office's builder, and a torn-down one has nothing left to do.
 * This is a selection over the API's list, not a recount of anything.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDownholeAssemblies, useDownholeRuns } from '../../../hooks/useDownhole';
import { DH_ASSEMBLY_STATUS_SKIN } from '../../../lib/downhole-labels';
import { formatDh } from '../../../lib/downhole-units';
import type { DhAssembly } from '../../../types/downhole';
import { DhBadge } from '../DownholeBits';
import { FieldCard, FieldEmpty, FieldQueryState, FieldSectionTitle, FieldTitle } from './FieldBits';

const GROUPS: { status: string; title: string; hint: string }[] = [
  { status: 'in_hole', title: 'In the hole', hint: 'Log or close a run' },
  { status: 'laid_down', title: 'Back from the well', hint: 'Waiting to be torn down' },
  { status: 'ready', title: 'Ready to go out', hint: 'Built and waiting for dispatch' },
];

export default function FieldAssembliesScreen() {
  const { user } = useAuth();
  const query = useDownholeAssemblies();
  const runsQuery = useDownholeRuns();
  const units = query.data?.meta.unit_system ?? null;

  // The assemblies where THIS hand has a run open — from the runs read, which
  // is where a tech is named. Not loaded yet or failed: no marks, never a guess.
  const mine = useMemo(() => {
    const ids = new Set<string>();
    for (const r of runsQuery.data?.data ?? []) {
      if (r.run_state === 'open' && r.field_tech_id && r.field_tech_id === user?.id) ids.add(r.assembly_id);
    }
    return ids;
  }, [runsQuery.data, user?.id]);

  const assemblies = query.data?.data ?? [];
  const groups = GROUPS.map((g) => ({
    ...g,
    rows: assemblies
      .filter((a) => a.status === g.status)
      .sort((a, b) => Number(mine.has(b.id)) - Number(mine.has(a.id))),
  }));
  const any = groups.some((g) => g.rows.length > 0);

  return (
    <div data-testid="downhole-field-assemblies">
      <FieldTitle title="Assemblies" />
      <FieldQueryState query={query} subject="assemblies">
        {!any ? (
          <FieldEmpty>No assembly is in the hole, back from the well, or ready to go out.</FieldEmpty>
        ) : (
          groups
            .filter((g) => g.rows.length > 0)
            .map((g) => (
              <section key={g.status} aria-label={g.title}>
                <FieldSectionTitle>
                  {g.title} · {g.rows.length}
                </FieldSectionTitle>
                <div className="space-y-2">
                  {g.rows.map((a) => (
                    <AssemblyCard key={a.id} a={a} mine={mine.has(a.id)} units={units} hint={g.hint} />
                  ))}
                </div>
              </section>
            ))
        )}
      </FieldQueryState>
    </div>
  );
}

function AssemblyCard({ a, mine, units, hint }: { a: DhAssembly; mine: boolean; units: Parameters<typeof formatDh>[2]; hint: string }) {
  return (
    <Link to={`/field/downhole/assemblies/${a.id}`} className="block">
      <FieldCard className="flex items-center gap-3 px-4 py-3 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{a.assembly_number}</span>
            <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.status} />
            {mine && (
              <span className="rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                Your run is open
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-sm font-bold text-white">{a.name}</p>
          <p className="truncate text-xs text-fieldiq-text">
            {[a.well_name, a.rig_name].filter(Boolean).join(' · ') || '—'}
          </p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {a.component_count} tools · {formatDh(a.total_length_ft, 'length', units)} · {hint}
          </p>
        </div>
        <ChevronRight className="size-5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </FieldCard>
    </Link>
  );
}
